import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { FAVICON_URL } from './utils/constant';
import { fetchRoutePrefix, isUrlPrefixValid, getCorrectedUrl, getPathWithPrefix } from './utils/prefixConfig';
import './scss/style.scss';
import Loader from './components/AppLoader';
import { tokenExchange } from './views/login/actions';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'react-redux-toastr';

const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'));
const Login = React.lazy(() => import('./views/login/Login'));
const ForgotPassword = React.lazy(() => import('./views/forgotPassword/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./views/resetPassword/ResetPassword'));

const extractQueryParam = (param) => {
  try {
    // Try extracting from `window.location.search`
    const urlParams = new URLSearchParams(window.location.search);
    const value = urlParams.get(param);
    if (value) return value.trim();

    // Try extracting from `window.location.href` (in case React strips params)
    const queryString = window.location.href.split("?")[1];
    if (queryString) {
      const params = new URLSearchParams(queryString);
      return params.get(param) ? params.get(param).trim() : null;
    }

    // Try extracting from `window.location.hash` (if using hash-based routing)
    const hashParams = new URLSearchParams(window.location.hash.split("?")[1]);
    return hashParams.get(param) ? hashParams.get(param).trim() : null;
  } catch (error) {
    return null;
  }
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [routePrefix, setRoutePrefix] = useState(null);
  const [prefixFetched, setPrefixFetched] = useState(false);
  const [isTokenExchanging, setIsTokenExchanging] = useState(false);

  const wellKnownRes = useSelector((state) => state.user.wellKnownRes);
  const authTokenRes = useSelector((state) => state.user.authResponse);
  const dispatch = useDispatch();

  useEffect(() => {
    const authorizationCode = extractQueryParam("code");
    const userEmail = localStorage.getItem("userEmail");
    const orgName = localStorage.getItem("orgName");
    if (!!authorizationCode && (!!userEmail || !!orgName)) {
      setIsTokenExchanging(true);
      const codeVerifier = localStorage.getItem('pkceCodeVerifier');
      const oidcNonce = localStorage.getItem('oidcNonce');
      localStorage.removeItem('pkceCodeVerifier');
      localStorage.removeItem('oidcNonce');
      const payload = orgName
        ? { code: authorizationCode, org: orgName, ...(codeVerifier && { code_verifier: codeVerifier }), ...(oidcNonce && { nonce: oidcNonce }) }
        : { code: authorizationCode, email: userEmail, ...(codeVerifier && { code_verifier: codeVerifier }), ...(oidcNonce && { nonce: oidcNonce }) };
      tokenExchange(payload, dispatch);
    }

    const initializeApp = async () => {
      try {
        //Fetch route prefix first
        const prefix = await fetchRoutePrefix();
        setRoutePrefix(prefix);
        setPrefixFetched(true);

        //Check if URL needs correction
        if (!isUrlPrefixValid()) {
          const correctedUrl = getCorrectedUrl();
          window.location.replace(correctedUrl);
          return; // Stop initialization, page will reload
        }

        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
          link = document.createElement('link');
          link.rel = 'icon';
          document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = FAVICON_URL;

      } catch (error) {
      }

      setIsLoading(false);
    };

    initializeApp();
  }, []);

  useEffect(() => {
    if (wellKnownRes) {
      if (wellKnownRes.error === false) {
        window.location.href = wellKnownRes.url;
      } else {
        toastr.error('Error!', wellKnownRes.message);
      }
    }
  }, [wellKnownRes]);

  useEffect(() => {
    if (authTokenRes) {
      if (authTokenRes.error === false) {
        localStorage.setItem('authToken', authTokenRes.data.authToken);
        localStorage.setItem('currentCustomer', authTokenRes.data.customer._id);
        localStorage.setItem('isOIDCLogin', 'true');
        // idb-next id_token — used ONLY by the <platform-waffle> launcher widget.
        // Separate from authToken (which drives this portal's own backend auth).
        if (authTokenRes.data.idbIdToken) {
          localStorage.setItem('idbIdToken', authTokenRes.data.idbIdToken);
        }
        if (authTokenRes.data.refreshToken) {
          localStorage.setItem('refreshToken', authTokenRes.data.refreshToken);
        }
        window.location.href = getPathWithPrefix('/dashboard');
      } else {
        toastr.error('Error!', authTokenRes.message);
        setIsTokenExchanging(false);
      }
    }
  }, [authTokenRes]);

  if (isLoading || !prefixFetched || isTokenExchanging) {
    return <Loader />;
  }

  // Compute basename for React Router
  const basename = routePrefix ? `/${routePrefix}` : '/';

  return (
    <BrowserRouter basename={basename}>
      <Suspense fallback={<Loader />}>
        <Routes>
            <Route path="/forgot/password" element={<ForgotPassword />} />
            <Route path="/reset/password/" element={<ResetPassword />} />
            <Route path="/reset/password/:resetCode" element={<ResetPassword />} />
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<DefaultLayout />} />
          </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
